import React, { FC, ReactElement } from 'react';
import { createStyles, makeStyles } from '@mui/styles';
import { Theme } from '@mui/material/styles';
import Accordion, { AccordionProps } from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import { clsx } from 'clsx';
// Composants
import Text from './text';
import SvgIcon from './icons/SvgIcon';
import { IconCommon } from '../resources/images';

interface AccordionBoxProps extends AccordionProps {
    title?: string;
    description?: string;
    subtitle?: string;
    nbItems?: number;
    isActive?: boolean;
    isLocked?: boolean;
    isError?: boolean;
    summary?: React.ReactNode;
    children: React.ReactNode;
    styleHistory?: boolean;
}

const AccordionBox: FC<AccordionBoxProps> = (props): ReactElement => {
    const classes = useStyles();
    const {
        children,
        title,
        description,
        subtitle,
        nbItems,
        isActive,
        isLocked,
        isError,
        summary,
        className,
        styleHistory,
        ...rest
    } = props;

    const Title = styleHistory ? Text.ShortRegularText16 : Text.ShortMediumText16;
    const expandIcon = styleHistory ? IconCommon.ChevronRight : IconCommon.ChevronDown;

    return (
        <Accordion
            {...rest}
            className={clsx(classes.accordionContainer, !styleHistory && classes.accordionContainerExpanded, className)}
        >
            <AccordionSummary
                id="accordionBox-header"
                aria-controls="accordionBox-content"
                className={styleHistory ? classes.styleHistory : classes.summaryContainer}
                // disabled={isLocked}
                expandIcon={<SvgIcon icon={expandIcon} color="black" fill="none" />}
            >
                {summary || (
                    <div className={classes.summaryHeader}>
                        <Title
                            text={`${title}${nbItems ? ` (${nbItems})` : ''}`}
                            className={classes.accordionBoxTitle}
                        />
                        {description && (
                            <Text.Description14
                                text={description}
                                customColor={isError ? 'red' : isActive ? 'lightblue' : 'grey'}
                            />
                        )}
                        {subtitle && <Text.Description14 text={subtitle} customColor="grey" />}
                    </div>
                )}
                {isLocked && (
                    <SvgIcon
                        icon={IconCommon.Lock}
                        size="mediumSmall"
                        color="grey"
                        stroke="none"
                        className={classes.iconLock}
                        overwritePathFill
                    />
                )}
            </AccordionSummary>
            <AccordionDetails
                id="accordionBox-content"
                className={styleHistory ? classes.styleContentHistory : classes.contentContainer}
            >
                <div className={isLocked ? classes.accordionDisabled : null}>{children}</div>
            </AccordionDetails>
        </Accordion>
    );
};

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        accordionDisabled: {
            opacity: 0.6,
            pointerEvents: 'none',
        },
        accordionContainer: {
            textAlign: 'initial',
            boxShadow: 'unset',
            borderRadius: 6,
            backgroundColor: theme.palette.background.default,
            '&:before': {
                height: 0,
            },
        },
        accordionContainerExpanded: {
            '&.Mui-expanded': {
                border: `1px solid ${theme.palette.colors.background.menuSeparator}`,
                borderRadius: 6,
            },
        },
        accordionBoxTitle: {
            fontWeight: 600 + '!important',
        },
        summaryContainer: {
            display: 'flex',
            alignItems: 'center',
            padding: 32,
            borderRadius: 6,
            flexDirection: 'row',
            backgroundColor: theme.palette.colors.background.filledForm,
            '& .MuiAccordionSummary-content': {
                margin: 0,
                alignItems: 'center',
                justifyContent: 'space-between',
            },
            '&.Mui-disabled': {
                opacity: '0.8',
            },
        },
        summaryHeader: {
            flexDirection: 'column',
        },
        iconLock: {
            marginRight: theme.spacing(3),
        },
        contentContainer: {
            overflow: 'auto',
            padding: '24px 32px 40px 32px',
        },
        styleHistory: {
            display: 'flex',
            alignItems: 'center',
            padding: 17,
            paddingRight: 0,
            paddingLeft: 4,
            '& .MuiAccordionSummary-content': {
                margin: 0,
                alignItems: 'center',
                justifyContent: 'space-between',
            },
            '&.Mui-disabled': {
                opacity: '0.8',
            },
            '& .MuiAccordionSummary-expandIconWrapper.Mui-expanded': {
                '-webkit-transform': 'rotate(-90deg)',
                '-moz-transform': 'rotate(-90deg)',
                '-ms-transform': 'rotate(-90deg)',
                transform: 'rotate(-90deg)',
            },
            '& .MuiAccordionSummary-expandIconWrapper': {
                '-webkit-transform': 'rotate(90deg)',
                '-moz-transform': 'rotate(90deg)',
                '-ms-transform': 'rotate(90deg)',
                transform: 'rotate(90deg)',
            },
        },
        styleContentHistory: {
            padding: 0,
            overflow: 'auto',
        },
    }),
);

export default AccordionBox;
